import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaceTabs } from '@uv-inserts/components/FaceTabs';
import { Visualiser, VisualiserGroup, VisualiserTextBox, VisualiserImageArea } from '@uv-inserts/components/Visualiser';
import PersonalisationSlotHeader from '../../PersonalisationSlotHeader';
import { FileInput } from '../../FileInput';
import { ToggleInput } from '../../ToggleInput';
import { usePersonalisationContext } from '../../../context/PersonalisationContext';
import { useLocale } from '../../../context/LocaleContext';
import {
  usePersonalisationUvInsertSlotViewModel,
  resolveAllowedUvInsertConfigs
} from './PersonalisationUvInsertSlot.vm';
import { buildUvInsertTextVisualiserSize } from '@shared/view/uv-inserts/build-text-visualiser-size';
import { getUvInsertPriceRange } from '@shared/view/uv-inserts/get-price-display';
import styles from './personalisation-uv-insert-slot.module.css';

/**
 * Converts a number or string dimension value to a CSS string.
 * Required because UVInsertConfig stores sizing as numbers (e.g. 200) or strings (e.g. "100%").
 * Always returns a string — falls back to the provided fallback or an empty string.
 *
 * @param {string|number|undefined} value - The value to convert.
 * @param {string} [fallback=''] - Fallback string if value is undefined.
 * @returns {string} The CSS string value.
 */
function toCssDimension(value, fallback = '') {
  if (value === undefined) return fallback;
  return String(value);
}

/**
 * Renders the UV insert editor UI (face tabs, visualiser, file upload).
 * Shared between single-config and multi-config paths — the caller is responsible
 * for opt-in gating and toggle display.
 *
 * @param {Object} props
 * @param {ReturnType<typeof usePersonalisationUvInsertSlotViewModel>} props.vm
 * @param {Object} props.slot
 */
function UvInsertEditorBody({ vm, slot }) {
  return (
    <div className={styles['personalisation__uv-insert-container']}>
      {vm.faceTabItems.length > 1 && (
        <FaceTabs
          tabs={vm.faceTabItems}
          activeIndex={vm.activeTabIndex}
          onActiveIndexChange={vm.setActiveTabIndex}
          fullWidth
        />
      )}

      <div
        role="tabpanel"
        id={`uv-insert-panel-${slot.id}-${vm.activeTabIndex}`}
        aria-labelledby={`uv-insert-tab-${slot.id}-${vm.activeTabIndex}`}
        className={styles['personalisation__uv-insert-tab-panel']}
      >
        <VisualiserGroup>
          {vm.facesToShow.map((face, fi) => {
            const frontThemeBackground =
              face.location === 'FRONT' && vm.selectedThemeImageSrc ? vm.selectedThemeImageSrc : null;

            return (
              <Visualiser
                key={`${face.location}-${fi}`}
                type={face.type}
                background={face.backgroundColor}
                backgroundImageUrl={frontThemeBackground ?? undefined}
                subtitle={face.subtitle}
              >
                {face.elementConfig.map((el, ei) => {
                  if (el.type === 'TEXT') {
                    const key = (el.lineItemPropertyKey ?? '').trim();
                    if (el.editable === false) {
                      if (!key) {
                        return (
                          <VisualiserTextBox
                            key={`static-${face.location}-${fi}-${ei}`}
                            font={el.font}
                            fontWeight={el.fontWeight}
                            fontSize={el.fontSize}
                            lineHeight={el.lineHeight}
                            color={el.color}
                            maxLength={el.maxLength}
                            maxLines={el.maxLines}
                            size={buildUvInsertTextVisualiserSize(el.size)}
                            position={{
                              top: toCssDimension(el.positioning?.top),
                              left: toCssDimension(el.positioning?.left),
                              right: toCssDimension(el.positioning?.right),
                              bottom: toCssDimension(el.positioning?.bottom)
                            }}
                            style={el.style}
                            userEditable={false}
                            value={el.value ?? ''}
                            setValue={() => {}}
                          />
                        );
                      }
                      const storedReadOnly = vm.textByKey[key];
                      const readOnlyDisplay =
                        storedReadOnly != null && storedReadOnly !== '' ? storedReadOnly : (el.value ?? '');
                      return (
                        <VisualiserTextBox
                          key={`${key}-${ei}`}
                          font={el.font}
                          fontWeight={el.fontWeight}
                          fontSize={el.fontSize}
                          lineHeight={el.lineHeight}
                          color={el.color}
                          maxLength={el.maxLength}
                          maxLines={el.maxLines}
                          size={buildUvInsertTextVisualiserSize(el.size)}
                          position={{
                            top: toCssDimension(el.positioning?.top),
                            left: toCssDimension(el.positioning?.left),
                            right: toCssDimension(el.positioning?.right),
                            bottom: toCssDimension(el.positioning?.bottom)
                          }}
                          style={el.style}
                          userEditable={false}
                          value={readOnlyDisplay}
                          setValue={(v) => vm.setText(key, v)}
                        />
                      );
                    }
                    return (
                      <VisualiserTextBox
                        key={`${key}-${ei}`}
                        font={el.font}
                        fontWeight={el.fontWeight}
                        fontSize={el.fontSize}
                        lineHeight={el.lineHeight}
                        color={el.color}
                        maxLength={el.maxLength}
                        maxLines={el.maxLines}
                        size={buildUvInsertTextVisualiserSize(el.size)}
                        position={{
                          top: toCssDimension(el.positioning?.top),
                          left: toCssDimension(el.positioning?.left),
                          right: toCssDimension(el.positioning?.right),
                          bottom: toCssDimension(el.positioning?.bottom)
                        }}
                        style={el.style}
                        value={vm.textByKey[key] ?? ''}
                        setValue={(v) => vm.setText(key, v)}
                      />
                    );
                  }

                  if (el.type === 'IMAGE_UPLOAD') {
                    const key = el.lineItemPropertyKey;
                    const file = vm.imageByKey[key] ?? null;
                    const transform = vm.imageTransformByKey[key] ?? { scale: 1, x: 0, y: 0 };
                    return (
                      <VisualiserImageArea
                        key={`${key}-${ei}`}
                        file={file}
                        backgroundColor={el.backgroundColor}
                        size={{
                          width: toCssDimension(el.size?.width, '200px'),
                          height: toCssDimension(el.size?.height, '200px')
                        }}
                        position={{
                          top: toCssDimension(el.positioning?.top),
                          left: toCssDimension(el.positioning?.left)
                        }}
                        borderRadius={el.borderRadius}
                        style={el.style}
                        scale={transform.scale}
                        x={transform.x}
                        y={transform.y}
                        onUpdate={(updates) => {
                          vm.setImageTransformByKey((prev) => ({
                            ...prev,
                            [key]: {
                              scale: updates.scale ?? transform.scale,
                              x: updates.x ?? transform.x,
                              y: updates.y ?? transform.y
                            }
                          }));
                        }}
                      />
                    );
                  }

                  return null;
                })}
              </Visualiser>
            );
          })}
        </VisualiserGroup>
      </div>

      {vm.imageUploadKeysForActiveTab.length > 0 && (
        <div className={styles['personalisation__uv-insert-file-upload-container']}>
          {vm.imageUploadKeysForActiveTab.map((imageKey) => (
            <FileInput
              key={imageKey}
              label={vm.imageUploadKeysForActiveTab.length === 1 ? 'Upload photo' : `Upload (${imageKey})`}
              onFileSelect={(value) => {
                const file =
                  value.userImage !== null && value.userImage.file instanceof File ? value.userImage.file : null;
                vm.setImageByKey((prev) => ({ ...prev, [imageKey]: file }));
              }}
              accept="png,jpg,jpeg,webp"
              disabled={false}
              buttonVariant="primary"
              buttonColor="orange"
              buttonSize="medium"
              maxFileSize={5 * 1024 * 1024}
              id={`uv-insert-img-${slot.id}-${imageKey}`}
              selectedFile={vm.imageByKey[imageKey] ?? null}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Routes UV insert slots by how many config handles admin allowed on this slot.
 *
 * @param {Object} props
 * @param {Object} props.slot
 * @returns {import('react').JSX.Element|null}
 */
export default function PersonalisationUvInsertSlot({ slot }) {
  const { resolvedUvInserts } = usePersonalisationContext();
  const configHandles = slot.uvInsertConfigHandles ?? [];

  if (!resolvedUvInserts) return null;

  if (configHandles.length === 0) {
    return (
      <div>
        <PersonalisationSlotHeader slot={slot} processHeading />
        <p>UV insert config not found for this slot. Check the slot configuration in admin.</p>
      </div>
    );
  }

  if (configHandles.length === 1) {
    return <PersonalisationUvInsertSlotSingle slot={slot} configHandle={configHandles[0]} />;
  }

  return <PersonalisationUvInsertSlotMulti slot={slot} configHandles={configHandles} />;
}

/**
 * Multiple allowed UV insert types — inline chooser that lets the customer pick one config,
 * then renders the full editor for the chosen config.
 *
 * The selected configHandle is stored in the selection payload so pricing, validation, and
 * cart encoding all resolve against the chosen config rather than a slot-level default.
 *
 * @param {Object} props
 * @param {Object} props.slot
 * @param {string[]} props.configHandles
 */
function PersonalisationUvInsertSlotMulti({ slot, configHandles }) {
  const { resolvedUvInserts, updateSelections, selections, setActiveUvInsertChooser } = usePersonalisationContext();
  const { formatPrice } = useLocale();

  const allowedConfigs = useMemo(
    () => resolveAllowedUvInsertConfigs({ handles: configHandles, resolvedUvInserts }),
    [configHandles, resolvedUvInserts]
  );

  // --- Price range across all allowed configs, driven purely by variant prices ---
  const priceRange = useMemo(
    () =>
      getUvInsertPriceRange({
        configs: allowedConfigs,
        configHandles: allowedConfigs.map((c) => c.handle)
      }),
    [allowedConfigs]
  );

  // True when different configs have different prices — drives per-card price display.
  const pricesVary = priceRange !== null && priceRange.minPounds !== priceRange.maxPounds;

  // Per-config cheapest theme price in pounds, only computed when prices vary.
  const configMinPricePounds = useMemo(() => {
    if (!pricesVary) return new Map();
    const map = new Map();
    for (const config of allowedConfigs) {
      const prices = config.themes
        .map((t) => parseFloat(String(t.price)))
        .filter((p) => !Number.isNaN(p) && p > 0)
        .map((p) => p / 100);
      if (prices.length > 0) map.set(config.handle, Math.min(...prices));
    }
    return map;
  }, [pricesVary, allowedConfigs]);

  const togglePriceText = useMemo(() => {
    if (!priceRange) return null;
    if (pricesVary) {
      return `- From ${formatPrice({ amount: priceRange.minPounds })}`;
    }
    return `+${formatPrice({ amount: priceRange.minPounds })}`;
  }, [priceRange, pricesVary, formatPrice]);

  const existingSelection = /** @type {import('./PersonalisationUvInsertSlot.vm').UvInsertSlotSelection | null} */ (
    selections?.slots.find((s) => s.id === slot.id)?.selected ?? null
  );

  // Restore the previously chosen config on back-navigation.
  const [chosenHandle, setChosenHandle] = useState(() => existingSelection?.configHandle ?? '');

  // Show the chooser list first; collapse to editor once the customer confirms a pick.
  // Skip straight to editor when restoring from back-navigation.
  const [showChooser, setShowChooser] = useState(() => existingSelection?.configHandle == null);

  const handleSelectionChange = useCallback(
    (selection) => {
      updateSelections({ slotId: slot.id, itemSelected: selection });
    },
    [slot.id, updateSelections]
  );

  const vm = usePersonalisationUvInsertSlotViewModel({
    resolvedUvInserts,
    configHandle: chosenHandle,
    isRequired: slot.isRequired,
    uvInsertVisibleByDefault: slot.uvInsertVisibleByDefault,
    isFreeSlot: slot.isFreeSlot,
    overridePrice: slot.overridePrice,
    existingSelection,
    onSelectionChange: handleSelectionChange
  });

  // Keep the footer chooser (themes/suggestions) in sync with the active config.
  // Suppress it while the type-selection chooser is visible — the strip should only
  // appear once the customer is on the editor page.
  useEffect(() => {
    if (!vm.optedIn || !vm.activeConfig || !resolvedUvInserts || showChooser) {
      setActiveUvInsertChooser((current) => (current?.slotId === slot.id ? null : current));
      return undefined;
    }

    setActiveUvInsertChooser({
      slotId: slot.id,
      config: vm.activeConfig,
      tabIndex: vm.activeTabIndex,
      suggestedMessaging: resolvedUvInserts.suggestedMessaging,
      selectedThemeIndex: vm.themeIndex,
      onThemeIndexChange: vm.setThemeIndex,
      themeChooserOpen: vm.themeChooserOpen,
      onThemeChooserOpenChange: vm.setThemeChooserOpen,
      selectedSuggestionGroupIndex: vm.selectedSuggestionGroupIndex,
      onSelectedSuggestionGroupIndexChange: vm.setSelectedSuggestionGroupIndex,
      suggestionChooserOpen: vm.suggestionChooserOpen,
      onSuggestionChooserOpenChange: vm.setSuggestionChooserOpen,
      onSuggestionPick: vm.onSuggestionPick
    });

    return () => {
      setActiveUvInsertChooser((current) => (current?.slotId === slot.id ? null : current));
    };
  }, [
    resolvedUvInserts,
    setActiveUvInsertChooser,
    showChooser,
    slot.id,
    vm.activeConfig,
    vm.activeTabIndex,
    vm.onSuggestionPick,
    vm.optedIn,
    vm.selectedSuggestionGroupIndex,
    vm.setSelectedSuggestionGroupIndex,
    vm.setSuggestionChooserOpen,
    vm.setThemeChooserOpen,
    vm.setThemeIndex,
    vm.suggestionChooserOpen,
    vm.themeChooserOpen,
    vm.themeIndex
  ]);

  if (!resolvedUvInserts) return null;

  if (allowedConfigs.length === 0) {
    return (
      <div>
        <PersonalisationSlotHeader slot={slot} processHeading />
        <p>UV insert config not found for: {configHandles.join(', ')}. Check the slot configuration in admin.</p>
      </div>
    );
  }

  return (
    <div>
      {vm.isSlotRequired && <PersonalisationSlotHeader slot={slot} processHeading />}

      {vm.isSlotRequired && vm.effectivePrice !== null && (
        <p className={styles['personalisation__uv-insert-price'] + ' body-xs'}>
          {vm.originalPrice !== null && <s>{formatPrice({ amount: vm.originalPrice })}</s>}{' '}
          {vm.effectivePrice === 0 ? 'Free' : `+${formatPrice({ amount: vm.effectivePrice })}`}
        </p>
      )}

      <div className={styles['personalisation__uv-insert']}>
        {!vm.isSlotRequired &&
          (() => {
            const title = slot.uvInsertPageId?.title || 'Add an insert';
            return (
              <ToggleInput
                id={`uv-insert-toggle-${slot.id}`}
                checked={vm.optedIn}
                onChange={vm.setOptedIn}
                description={slot.uvInsertPageId?.body}
                ariaLabel={togglePriceText ? `${title} ${togglePriceText}` : title}
                label={
                  <>
                    {title}
                    {togglePriceText && <span>&nbsp;{togglePriceText}</span>}
                  </>
                }
              />
            );
          })()}

        {vm.optedIn && (
          <>
            {showChooser ? (
              /* Step 1 - choose an insert type. */
              <div key="chooser" className={styles['personalisation__uv-insert-type-chooser']}>
                {allowedConfigs.map((config) => (
                  <button
                    key={config.handle}
                    type="button"
                    className={
                      styles['personalisation__uv-insert-type-option'] +
                      (config.handle === chosenHandle
                        ? ' ' + styles['personalisation__uv-insert-type-option--selected']
                        : '')
                    }
                    onClick={() => {
                      setChosenHandle(config.handle);
                      setShowChooser(false);
                    }}
                    aria-label={`Select ${config.title}`}
                    aria-selected={config.handle === chosenHandle}
                  >
                    {config.thumbnailSrc ? (
                      <img
                        src={config.thumbnailSrc}
                        className={styles['personalisation__uv-insert-type-option-thumbnail']}
                        width={48}
                        height={48}
                        aria-hidden="true"
                        alt=""
                      />
                    ) : (
                      <span
                        className={styles['personalisation__uv-insert-type-option-placeholder']}
                        aria-hidden="true"
                      />
                    )}
                    <div className={styles['personalisation__uv-insert-option-content']}>
                      {config.title && (
                        <p className={styles['personalisation__uv-insert-type-option-title'] + ' body-sm font-medium'}>
                          {config.title}
                        </p>
                      )}
                      {config.description && <p className="body-xs">{config.description}</p>}
                      {pricesVary && configMinPricePounds.has(config.handle) && (
                        <p
                          className={styles['personalisation__uv-insert-type-option-price'] + ' body-xs'}
                          aria-label="Additional price"
                        >
                          +{formatPrice({ amount: configMinPricePounds.get(config.handle) })}
                        </p>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              /* Step 2 — edit the chosen insert type. */
              <div key="editor" className={styles['personalisation__uv-insert-editor-wrapper']}>
                <div className={styles['personalisation__uv-insert-change-row']}>
                  <span className="body-sm">
                    {allowedConfigs.find((c) => c.handle === chosenHandle)?.title || chosenHandle}
                  </span>
                  <button
                    type="button"
                    aria-label="Change insert type"
                    className={styles['personalisation__uv-insert-change-btn'] + ' button-reset body-sm'}
                    onClick={() => setShowChooser(true)}
                  >
                    Change
                  </button>
                </div>
                <UvInsertEditorBody vm={vm} slot={slot} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Single allowed UV insert — full editor (unchanged from pre–multi-choice behaviour).
 *
 * @param {Object} props
 * @param {Object} props.slot
 * @param {string} props.configHandle
 */
function PersonalisationUvInsertSlotSingle({ slot, configHandle }) {
  const { resolvedUvInserts, updateSelections, selections, setActiveUvInsertChooser } = usePersonalisationContext();
  const { formatPrice } = useLocale();

  const existingSelection = /** @type {import('./PersonalisationUvInsertSlot.vm').UvInsertSlotSelection | null} */ (
    selections?.slots.find((s) => s.id === slot.id)?.selected ?? null
  );

  const handleSelectionChange = useCallback(
    (selection) => {
      updateSelections({ slotId: slot.id, itemSelected: selection });
    },
    [slot.id, updateSelections]
  );

  const vm = usePersonalisationUvInsertSlotViewModel({
    resolvedUvInserts,
    configHandle,
    isRequired: slot.isRequired,
    uvInsertVisibleByDefault: slot.uvInsertVisibleByDefault,
    isFreeSlot: slot.isFreeSlot,
    overridePrice: slot.overridePrice,
    existingSelection,
    onSelectionChange: handleSelectionChange
  });

  useEffect(() => {
    if (!vm.optedIn || !vm.activeConfig || !resolvedUvInserts) {
      setActiveUvInsertChooser((current) => (current?.slotId === slot.id ? null : current));
      return undefined;
    }

    setActiveUvInsertChooser({
      slotId: slot.id,
      config: vm.activeConfig,
      tabIndex: vm.activeTabIndex,
      suggestedMessaging: resolvedUvInserts.suggestedMessaging,
      selectedThemeIndex: vm.themeIndex,
      onThemeIndexChange: vm.setThemeIndex,
      themeChooserOpen: vm.themeChooserOpen,
      onThemeChooserOpenChange: vm.setThemeChooserOpen,
      selectedSuggestionGroupIndex: vm.selectedSuggestionGroupIndex,
      onSelectedSuggestionGroupIndexChange: vm.setSelectedSuggestionGroupIndex,
      suggestionChooserOpen: vm.suggestionChooserOpen,
      onSuggestionChooserOpenChange: vm.setSuggestionChooserOpen,
      onSuggestionPick: vm.onSuggestionPick
    });

    return () => {
      setActiveUvInsertChooser((current) => (current?.slotId === slot.id ? null : current));
    };
  }, [
    resolvedUvInserts,
    setActiveUvInsertChooser,
    slot.id,
    vm.activeConfig,
    vm.activeTabIndex,
    vm.onSuggestionPick,
    vm.optedIn,
    vm.selectedSuggestionGroupIndex,
    vm.setSelectedSuggestionGroupIndex,
    vm.setSuggestionChooserOpen,
    vm.setThemeChooserOpen,
    vm.setThemeIndex,
    vm.suggestionChooserOpen,
    vm.themeChooserOpen,
    vm.themeIndex
  ]);

  // Data not yet loaded from bootstrap — render nothing until context is ready.
  if (!resolvedUvInserts) return null;

  // The config handle on this slot doesn't match any resolved UV insert config.
  if (!vm.activeConfig) {
    return (
      <div>
        <PersonalisationSlotHeader slot={slot} processHeading />
        <p>UV insert config &ldquo;{configHandle}&rdquo; not found. Check the slot configuration.</p>
      </div>
    );
  }

  return (
    <div>
      {/*
        Required slots show the standard slot header (heading + optional help link).
        Optional slots use the ToggleInput as their heading row — showing the slot header
        separately would double-display uiHeading.
      */}
      {vm.isSlotRequired && <PersonalisationSlotHeader slot={slot} processHeading />}

      {/* Price display for required slots (shown below the slot header). */}
      {vm.isSlotRequired && vm.effectivePrice !== null && (
        <p className={styles['personalisation__uv-insert-price'] + ' body-xs'}>
          {vm.originalPrice !== null && <s>{formatPrice({ amount: vm.originalPrice })}</s>}{' '}
          {vm.effectivePrice === 0 ? 'Free' : `+${formatPrice({ amount: vm.effectivePrice })}`}
        </p>
      )}
      <div className={styles['personalisation__uv-insert']}>
        {/* Optional slots: toggle with price inline in the label row. Required slots are always opted in. */}
        {!vm.isSlotRequired &&
          (() => {
            const title = slot.uvInsertPageId?.title || 'Add an insert';
            const priceText =
              vm.effectivePrice === null
                ? ''
                : vm.effectivePrice === 0
                  ? ' Free'
                  : ` +${formatPrice({ amount: vm.effectivePrice })}`;

            return (
              <ToggleInput
                id={`uv-insert-toggle-${slot.id}`}
                checked={vm.optedIn}
                onChange={vm.setOptedIn}
                description={slot.uvInsertPageId?.body}
                ariaLabel={`${title}${priceText}`}
                label={
                  <>
                    {title}
                    {vm.effectivePrice !== null && (
                      <span>
                        &nbsp;
                        {vm.originalPrice !== null && <s>{formatPrice({ amount: vm.originalPrice })}</s>}
                        {vm.effectivePrice === 0 ? ' Free' : `+${formatPrice({ amount: vm.effectivePrice })}`}
                      </span>
                    )}
                  </>
                }
              />
            );
          })()}

        {/* Only render the insert UI when the user has opted in (or the slot is required). */}
        {vm.optedIn && <UvInsertEditorBody vm={vm} slot={slot} />}
      </div>
    </div>
  );
}
